import { Consumptions } from "@/components/balances/Consumptions";
import { Alert } from "@/components/fun-ui/Alert";
import { DeleteIcon } from "@/components/fun-ui/icons/DeleteIcon";
import { HelpCircleIcon } from "@/components/fun-ui/icons/HelpCircleIcon";
import { PlusIcon } from "@/components/fun-ui/icons/PlusIcon";
import { Tip } from "@/components/fun-ui/Tip";
import { useSubjectId } from "@/components/subjects/hooks/usePathParams";
import { cn } from "@/lib/utils";
import { useAiCol, useUserVip } from "@/stores/appStore";
import { Col, SetPaperColsMode } from "@/stores/slices/paperSlice";
import { useMemo, Fragment, useRef, useState } from "react";
import { FloatingPortal, offset } from "@floating-ui/react";
import { useFloatingCard } from "@/hooks/useFloatingCard";
import { AIColLimitFloatingCard, LimitType } from "./AIColLimitFloatingCard";
import { CreateCustomAIColButton } from "./CreateCustomAICol";
import { MinusIcon } from "@/components/fun-ui/icons";
import { Separator } from "@/components/ui/separator";
import { chartColKeys, chartColTips, LANG_LIST } from "./lib/aiColsConst";

export type AIColsSelectorProps = {
  cols: Col[];
  onSelected: (colKeys: string[], cols: Col[], mode?: SetPaperColsMode) => Promise<void>;
  className?: string;
};

export function AIColsSelector({
  cols,
  onSelected,
  className = '',
}: AIColsSelectorProps) {
  const [limitType, setLimitType] = useState<LimitType>('custom');
  const {
    open,
    refs,
    floatingStyles,
    getReferenceHoverProps,
    getFloatingHoverProps,
  } = useFloatingCard({
    placement: "left-start",
    middleware: [offset(6)],
  });

  const createBtnRef = useRef<HTMLDivElement>(null);

  const allColsList = useMemo(() => [{
    title: '创建的 AI 表格列：',
    currentcols: cols.filter(({ isCustom, selected }) => isCustom && !selected),
  }, {
    title: '智能解析表格列：',
    currentcols: cols.filter(({ key, selected }) => !selected && chartColKeys.includes(key)),
  }, {
    title: '预设的 AI 表格列：',
    currentcols: cols.filter(({ isCustom, selected, key }) => !isCustom && !selected && !chartColKeys.includes(key)),
  }, {
    title: '已开启的表格列',
    currentcols: cols.filter(({ selected }) => selected),
  }], [cols]);

  const { deleteAiCol } = useAiCol();
  const subjectId = useSubjectId();

  const {
    vip,
    limits
  } = useUserVip();

  const isAiColsLimited = useMemo(() => {
    if (limits) {
      if (limits.aiColLimit < 0) {
        return false;
      } else {
        // 是否超出上限
        // 数量
        const counts = cols.filter(({ selected, key }) => (
          // 已经选中，并且不是图表列
          selected && !chartColKeys.includes(key)
        )).length;
        // 数量大于等于限制数
        return counts >= limits.aiColLimit;
      }
    }
    return true;
  }, [cols, limits]);

  if (!limits) {
    return (
      <></>
    );
  }

  return (
    <>
      {cols.filter(({ isCustom }) => isCustom).length < limits.customLimit ? (
        <CreateCustomAIColButton />
      ) : (
        <div
          ref={createBtnRef}
          className="mt-3 max-w-fit"
          {...getReferenceHoverProps(() => {
            setLimitType('custom');
          })}
        >
          <CreateCustomAIColButton disabled={true} />
        </div>
      )}
      <ul className={
        cn(
          "flex flex-col",
          "max-h-fit",
          className,
        )
      }>
        {
          allColsList.map(({ title, currentcols }) => {
            return (
              <Fragment key={title}>
                {
                  currentcols.length ? (
                    <>
                      {
                        currentcols?.[0]?.selected ? (
                          <li className="mt-4"><Separator /></li>
                        ) : null
                      }
                      <li className="text-sm py-1.5 mt-3.5">{title}</li>
                    </>
                  ) : null
                }
                {
                  currentcols.map((col) => {
                    const lang = LANG_LIST.find(item => item.value === col.lang)?.label;
                    const isChartCol = chartColKeys.includes(col.key);
                    const isSelected = col.selected;
                    // 没有选中，并且没有超出上限，并且不是图表列
                    const isDisabled = !isSelected && isAiColsLimited && !isChartCol;
                    return (
                      <li
                        key={col.key}
                        data-target
                        className={cn(
                          "space-x-2 flex items-center justify-between",
                        )}
                      >
                        <div className="flex flex-1 items-center">
                          <div
                            className="flex items-center w-fit cursor-pointer"
                          >
                            <div
                              className={cn(
                                "py-1.5 inline-flex items-center text-primary hover:underline underline-offset-4",
                                (isDisabled) && "opacity-50 hover:no-underline"
                              )}
                              {...(isDisabled ? getReferenceHoverProps(() => {
                                setLimitType('aicols');
                              }) : {})}
                              onClick={async () => {
                                if (isDisabled) {
                                  return;
                                }
                                const selectedCols = cols.filter(({ key: colKey }) => {
                                  return colKey === col.key;
                                });
                                const colKeys = selectedCols.map(({ key }) => key);
                                await onSelected(colKeys, selectedCols, isSelected ? 'del' : 'add');
                              }}
                            >
                              {
                                isSelected ? (
                                  <MinusIcon />
                                ) : (
                                  <PlusIcon className="-mt-[1px]" />
                                )
                              }
                              <span className="inline-block leading-6">{col.name}</span>
                            </div>
                          </div>
                          {
                            col.isCustom ? (
                              <>
                                <Tip
                                  className="cursor-help ml-3 text-muted-foreground"
                                  side="top"
                                  label={`${lang}｜${col.desc}`}
                                >
                                  <HelpCircleIcon />
                                </Tip>
                                <Alert
                                  title="删除 AI 表格列"
                                  description={
                                    <>
                                      <span className="block text-sm">确认删除 AI 表格列“{col.name}”吗？删除后该列数据将丢失且不可找回</span>
                                      <span className="block text-sm text-annotation-foreground">表格列输出语言：{lang}</span>
                                      <span className="block text-sm text-annotation-foreground">表格列提取要求：{col.desc}</span>
                                    </>
                                  }
                                  confirmLabel="删除"
                                  confirmVariant="destructive"
                                  asyncConfirm={async () => {
                                    await deleteAiCol({
                                      colId: col.colId,
                                    }, subjectId);
                                  }}>
                                  <DeleteIcon
                                    className="ml-3 text-muted-foreground cursor-pointer"
                                  />
                                </Alert>
                              </>
                            ) : null
                          }
                          {
                            !isSelected && isChartCol ? (
                              <Tip
                                className="cursor-help ml-3 text-muted-foreground"
                                side="top"
                                label={chartColTips[col.key as (keyof typeof chartColTips)]}
                              >
                                <HelpCircleIcon />
                              </Tip>
                            ) : null
                          }
                        </div>
                        {
                          isSelected ? null : (
                            <Consumptions className="ml-2" scene={{
                              label: col.cost ? `新生成 ${col.cellCount} 个单元格消耗 ${col.cost} 个飞豆` : '无需消耗飞豆',
                              price: col.cost,
                            }} />
                          )
                        }
                      </li>
                    )
                  })
                }
              </Fragment>
            )
          })
        }
        <FloatingPortal>
          {open && (
            <div
              className="z-[51]"
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingHoverProps()}
            >
              <AIColLimitFloatingCard limitType={limitType} />
            </div>
          )}
        </FloatingPortal>
      </ul>
    </>

  );
}

